import {useCallback, useEffect, useMemo, useState} from "react";
import {SquadflixHeaderType} from "../../user-app-config/hooks";
import {SquadflixHeaderNewsItem} from "../../../apis/squadflix-api/types";
import {SquadflixApi} from "../../../apis/squadflix-api/SquadflixApi";
import {fetchSquadflixAvailableItems, fetchTrendingMovies, fetchTrendingSeries} from "../api/cap17drive-api";
import UseSquadflixNavigate from "../squadflixHome/useSquadflixNavigate";

export const useTrendingHeaderItems = (headerType: SquadflixHeaderType, autoRotateMs = 5000) => {
    const [tmdbTrendingMovies, setTmdbTrendingMovies] = useState<any[]>([]);
    const [squadflixHeaderNews, setSquadflixHeaderNews] = useState<SquadflixHeaderNewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const squadflixNav = UseSquadflixNavigate({});

    const allItems = useMemo(() => {
        return [...squadflixHeaderNews, ...tmdbTrendingMovies];
    }, [squadflixHeaderNews, tmdbTrendingMovies]);

    useEffect(() => {
        if (!squadflixHeaderNews.length) {
            SquadflixApi.getSquadflixHeaderNews().then((data) => {
                setSquadflixHeaderNews(data || []);
            }).catch(() => {});
        }

        setLoading(true);
        const normalizedType =
            headerType !== undefined && headerType !== null
                ? Number(headerType)
                : SquadflixHeaderType.TRENDING_ITEMS;

        if (normalizedType === SquadflixHeaderType.TRENDING_ITEMS) {
            const fetchMovies = fetchTrendingMovies().then((data) => {
                return data?.results?.map((x: any) => ({
                    ...x,
                    isMovie: true
                })) ?? [];
            });
            const fetchSeries = fetchTrendingSeries().then((data) => {
                return data?.results ?? [];
            });

            Promise.all([fetchMovies, fetchSeries]).then(res => {
                const combined = [...res[0], ...res[1]];
                combined.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
                setTmdbTrendingMovies(combined.slice(0, 20));
            }).finally(() => {
                setLoading(false);
            });
        } else if (normalizedType === SquadflixHeaderType.TRENDING_MOVIES) {
            fetchTrendingMovies().then((data) => {
                setTmdbTrendingMovies(data?.results?.map((x: any) => ({
                    ...x,
                    isMovie: true
                }))?.sort((a: any, b: any) => (b.popularity || 0) - (a.popularity || 0)) ?? []);
            }).finally(() => {
                setLoading(false);
            });
        } else if (normalizedType === SquadflixHeaderType.TRENDING_SERIES) {
            fetchTrendingSeries().then((data) => {
                setTmdbTrendingMovies(data?.results?.sort((a: any, b: any) => (b.popularity || 0) - (a.popularity || 0)) ?? []);
            }).finally(() => {
                setLoading(false);
            });
        } else if (normalizedType === SquadflixHeaderType.SQUADFLIX_ORIGINALS) {
            const fetchMovies = fetchSquadflixAvailableItems({type: "movies", mode: null}).then((data) => {
                return data?.results?.map((x: any) => ({
                    ...x,
                    isMovie: true
                })) ?? [];
            });
            const fetchSeries = fetchSquadflixAvailableItems({type: "series", mode: null}).then((data) => {
                return data?.results ?? [];
            });

            Promise.all([fetchMovies, fetchSeries]).then(res => {
                const combined = [...res[0], ...res[1]];
                combined.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
                setTmdbTrendingMovies(combined);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [headerType]);

    useEffect(() => {
        if (!autoRotateMs || !allItems.length) return;
        const timeout = setTimeout(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % allItems.length);
        }, autoRotateMs);
        return () => clearTimeout(timeout);
    }, [currentIndex, allItems.length, autoRotateMs]);

    const nextSlide = useCallback(() => {
        if (!allItems.length) return;
        setCurrentIndex((prev) => (prev + 1) % allItems.length);
    }, [allItems.length]);

    const prevSlide = useCallback(() => {
        if (!allItems.length) return;
        setCurrentIndex((prev) => (prev - 1 + allItems.length) % allItems.length);
    }, [allItems.length]);

    const openItem = useCallback((item: any) => {
        if (!item) return;
        if (item.order) {
            if (item.tmdbId && item.type) {
                if (item.type.toLowerCase() === "movies") {
                    squadflixNav.navigateToMovie({id: item.tmdbId} as any);
                    return;
                } else if (item.type.toLowerCase() === "series") {
                    squadflixNav.navigateToSerie({id: item.tmdbId} as any);
                    return;
                }
            }
            if (item?.id) {
                squadflixNav.navigateToMovie(item);
            }
        } else if (item.isMovie || item.media_type === "movie") {
            squadflixNav.navigateToMovie(item);
        } else {
            squadflixNav.navigateToSerie(item);
        }
    }, [squadflixNav]);

    return {
        allItems,
        tmdbTrendingMovies,
        squadflixHeaderNews,
        loading,
        currentIndex,
        setCurrentIndex,
        currentItem: allItems[currentIndex] ?? null,
        nextSlide,
        prevSlide,
        openItem
    };
};

export default useTrendingHeaderItems;
